import { contrastRatio, meetsWCAGAA } from './contrast';


const normaliseHex = value => {
  if (typeof value !== 'string') return '';
  const trimmed = value.trim();
  if (!trimmed) return '';
  if (!trimmed.startsWith('#')) return trimmed.toLowerCase();
  if (trimmed.length === 4) {
    const r = trimmed[1];
    const g = trimmed[2];
    const b = trimmed[3];
    return `#${r}${r}${g}${g}${b}${b}`.toLowerCase();
  }
  return trimmed.toLowerCase();
};

const coloursMatch = (a, b) => normaliseHex(a) === normaliseHex(b);


const HEX_FULL_REGEX = /^#[0-9a-f]{6}$/i;
const HEX_SHORT_REGEX = /^#[0-9a-f]{3}$/i;
const BARE_HEX_FULL_REGEX = /^[0-9a-f]{6}$/i;
const BARE_HEX_SHORT_REGEX = /^[0-9a-f]{3}$/i;
const CONTRAST_IMPROVEMENT_EPSILON = 0.05;
const clamp = (value, min, max) => Math.min(Math.max(value, min), max);

const normaliseHexStrict = value => {
  if (typeof value !== 'string') return null;
  const trimmed = value.trim().toLowerCase();
  if (!trimmed) return null;
  if (HEX_FULL_REGEX.test(trimmed)) return trimmed;
  if (HEX_SHORT_REGEX.test(trimmed)) {
    const [, r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  if (BARE_HEX_FULL_REGEX.test(trimmed)) return `#${trimmed}`;
  if (BARE_HEX_SHORT_REGEX.test(trimmed)) {
    const [r, g, b] = trimmed;
    return `#${r}${r}${g}${g}${b}${b}`;
  }
  return null;
};

const hexToRgb = hex => {
  const normalised = normaliseHexStrict(hex);
  if (!normalised) return null;
  const value = parseInt(normalised.slice(1), 16);
  return {
    r: (value >> 16) & 255,
    g: (value >> 8) & 255,
    b: value & 255,
  };
};

const rgbToHex = ({ r, g, b }) => {
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return null;
  const channelToHex = channel => {
    const clamped = Math.round(clamp(channel, 0, 255));
    return clamped.toString(16).padStart(2, '0');
  };
  return `#${channelToHex(r)}${channelToHex(g)}${channelToHex(b)}`;
};

const rgbToHsl = ({ r, g, b }) => {
  if (!Number.isFinite(r) || !Number.isFinite(g) || !Number.isFinite(b)) return null;
  const rn = clamp(r, 0, 255) / 255;
  const gn = clamp(g, 0, 255) / 255;
  const bn = clamp(b, 0, 255) / 255;
  const max = Math.max(rn, gn, bn);
  const min = Math.min(rn, gn, bn);
  let h = 0;
  let s = 0;
  const l = (max + min) / 2;

  if (max !== min) {
    const d = max - min;
    s = l > 0.5 ? d / (2 - max - min) : d / (max + min);
    switch (max) {
      case rn:
        h = (gn - bn) / d + (gn < bn ? 6 : 0);
        break;
      case gn:
        h = (bn - rn) / d + 2;
        break;
      default:
        h = (rn - gn) / d + 4;
        break;
    }
    h /= 6;
  }

  const hue = ((h * 360) % 360 + 360) % 360;
  return { h: hue, s, l };
};

const hslToRgb = ({ h, s, l }) => {
  if (!Number.isFinite(h) || !Number.isFinite(s) || !Number.isFinite(l)) return null;
  const hue = (((h % 360) + 360) % 360) / 360;
  const saturation = clamp(s, 0, 1);
  const lightness = clamp(l, 0, 1);

  if (saturation === 0) {
    const value = Math.round(lightness * 255);
    return { r: value, g: value, b: value };
  }

  const q =
    lightness < 0.5
      ? lightness * (1 + saturation)
      : lightness + saturation - lightness * saturation;
  const p = 2 * lightness - q;

  const hue2rgb = t => {
    let temp = t;
    if (temp < 0) temp += 1;
    if (temp > 1) temp -= 1;
    if (temp < 1 / 6) return p + (q - p) * 6 * temp;
    if (temp < 1 / 2) return q;
    if (temp < 2 / 3) return p + (q - p) * (2 / 3 - temp) * 6;
    return p;
  };

  return {
    r: Math.round(clamp(hue2rgb(hue + 1 / 3) * 255, 0, 255)),
    g: Math.round(clamp(hue2rgb(hue) * 255, 0, 255)),
    b: Math.round(clamp(hue2rgb(hue - 1 / 3) * 255, 0, 255)),
  };
};

const findAccessibleVariant = (baseColor, backgroundColor, targetRatio = 4.5) => {
  const baseHex = normaliseHexStrict(baseColor);
  const bgHex = normaliseHexStrict(backgroundColor);
  if (!baseHex || !bgHex) return null;

  const baseRgb = hexToRgb(baseHex);
  const baseHsl = baseRgb ? rgbToHsl(baseRgb) : null;
  if (!baseHsl) return null;

  const baseRatio = contrastRatio(baseHex, bgHex);
  const baseline = typeof baseRatio === 'number' ? baseRatio : 0;

  const maxSteps = 40;
  let bestTargetCandidate = null;
  let bestImprovement = null;

  const considerCandidate = candidate => {
    if (!candidate) return;
    if (candidate.meetsTarget) {
      if (
        !bestTargetCandidate ||
        candidate.delta < bestTargetCandidate.delta - 1e-4 ||
        (Math.abs(candidate.delta - bestTargetCandidate.delta) < 1e-4 &&
          candidate.ratio > bestTargetCandidate.ratio + 1e-2)
      ) {
        bestTargetCandidate = candidate;
      }
      return;
    }

    if (candidate.ratio > baseline + 1e-2) {
      if (
        !bestImprovement ||
        candidate.ratio > bestImprovement.ratio + 1e-2 ||
        (Math.abs(candidate.ratio - bestImprovement.ratio) < 1e-2 &&
          candidate.delta < bestImprovement.delta)
      ) {
        bestImprovement = candidate;
      }
    }
  };

  const directions = ['lighten', 'darken'];
  directions.forEach(direction => {
    for (let step = 1; step <= maxSteps; step += 1) {
      const fraction = step / maxSteps;
      const adjustedLightness =
        direction === 'lighten'
          ? baseHsl.l + (1 - baseHsl.l) * fraction
          : baseHsl.l - baseHsl.l * fraction;
      const candidateRgb = hslToRgb({ h: baseHsl.h, s: baseHsl.s, l: clamp(adjustedLightness, 0, 1) });
      const candidateHex = candidateRgb ? rgbToHex(candidateRgb) : null;
      if (!candidateHex) continue;
      const candidateRatio = contrastRatio(candidateHex, bgHex);
      if (candidateRatio === null) continue;

      const candidate = {
        hex: candidateHex,
        ratio: candidateRatio,
        delta: Math.abs(adjustedLightness - baseHsl.l),
        meetsTarget: candidateRatio >= targetRatio,
      };

      considerCandidate(candidate);
      if (candidate.meetsTarget) break;
    }
  });

  return bestTargetCandidate || bestImprovement;
};

const resolveContrastPair = ({
  foreground,
  background,
  targetRatio = 4.5,
  allowBackgroundAdjust = true,
}) => {
  const foregroundHex = normaliseHexStrict(foreground);
  const backgroundHex = normaliseHexStrict(background);

  if (!foregroundHex || !backgroundHex) {
    return { changed: false };
  }

  const currentRatio = contrastRatio(foregroundHex, backgroundHex) ?? 0;
  if (currentRatio >= targetRatio) {
    return { changed: false };
  }

  const foregroundCandidate = findAccessibleVariant(
    foregroundHex,
    backgroundHex,
    targetRatio
  );
  const backgroundCandidate = allowBackgroundAdjust
    ? findAccessibleVariant(backgroundHex, foregroundHex, targetRatio)
    : null;

  const pickCandidate = () => {
    const fgTarget = foregroundCandidate && foregroundCandidate.meetsTarget;
    const bgTarget = backgroundCandidate && backgroundCandidate.meetsTarget;

    if (fgTarget && bgTarget) {
      return foregroundCandidate.delta <= backgroundCandidate.delta
        ? { target: 'foreground', ...foregroundCandidate }
        : { target: 'background', ...backgroundCandidate };
    }
    if (fgTarget) return { target: 'foreground', ...foregroundCandidate };
    if (bgTarget) return { target: 'background', ...backgroundCandidate };

    const fgImproves =
      foregroundCandidate &&
      foregroundCandidate.ratio > currentRatio + CONTRAST_IMPROVEMENT_EPSILON;
    const bgImproves =
      backgroundCandidate &&
      backgroundCandidate.ratio > currentRatio + CONTRAST_IMPROVEMENT_EPSILON;

    if (fgImproves && bgImproves) {
      return foregroundCandidate.ratio >= backgroundCandidate.ratio
        ? { target: 'foreground', ...foregroundCandidate }
        : { target: 'background', ...backgroundCandidate };
    }
    if (fgImproves) return { target: 'foreground', ...foregroundCandidate };
    if (bgImproves) return { target: 'background', ...backgroundCandidate };
    return null;
  };

  const chosen = pickCandidate();
  if (!chosen) {
    return { changed: false };
  }

  if (chosen.target === 'foreground') {
    if (coloursMatch(foreground, chosen.hex)) {
      return { changed: false };
    }
    return {
      changed: true,
      target: 'foreground',
      value: chosen.hex,
      ratio: chosen.ratio,
    };
  }

  if (coloursMatch(background, chosen.hex)) {
    return { changed: false };
  }
  return {
    changed: true,
    target: 'background',
    value: chosen.hex,
    ratio: chosen.ratio,
  };
};

export const buildContrastChecks = settings => {
  if (!settings) return [];
  const candidates = [];
  const pushCandidate = (id, label, foreground, background, context) => {
    if (!foreground || !background) return;
    candidates.push({ id, label, foreground, background, context });
  };

  pushCandidate(
    'body-text',
    'Body text vs email background',
    settings.bodyTextColor,
    settings.bodyBackgroundColor,
    'General copy'
  );
  pushCandidate(
    'link-text',
    'Link text vs email background',
    settings.linkColor,
    settings.bodyBackgroundColor,
    'Inline links'
  );
  pushCandidate(
    'footer-text',
    'Footer text vs footer background',
    settings.footerTextColor,
    settings.footerBackgroundColor,
    'Footer content'
  );

  if (Array.isArray(settings.ctaButtons)) {
    settings.ctaButtons.forEach((button, index) => {
      pushCandidate(
        `cta-${index}`,
        `CTA button ${index + 1} text`,
        button?.textColor,
        button?.backgroundColor,
        button?.text || button?.url || 'CTA button'
      );
    });
  }

  if (settings.includeCalendarButton) {
    pushCandidate(
      'calendar-button',
      'Calendar button text',
      settings.calendarButtonTextColor,
      settings.calendarButtonBackgroundColor,
      settings.calendarButtonText || 'Calendar button'
    );
  }

  if (Array.isArray(settings.sectionTitles)) {
    settings.sectionTitles.slice(0, 5).forEach((section, index) => {
      const label = section?.text || `Section heading ${index + 1}`;
      pushCandidate(
        `section-${index}`,
        `Section heading ${index + 1}`,
        section?.color,
        settings.bodyBackgroundColor,
        label
      );
    });
  }

  return candidates.map(item => {
    const ratio = contrastRatio(item.foreground, item.background);
    return {
      ...item,
      ratio,
      passes: ratio !== null ? meetsWCAGAA(ratio) : false,
    };
  });
};

export const runHeadingAudit = html => {
  if (!html) {
    return [
      {
        type: 'warning',
        message: 'Preview unavailable. Unable to inspect heading structure.',
      },
    ];
  }
  try {
    const parser = new DOMParser();
    const doc = parser.parseFromString(html, 'text/html');
    const headings = Array.from(doc.querySelectorAll('h1, h2, h3, h4, h5, h6'));
    const issues = [];
    if (headings.length === 0) {
      issues.push({
        type: 'warning',
        message: 'No headings found. Add descriptive headings for screen readers.',
      });
      return issues;
    }
    const firstLevel = parseInt(headings[0].tagName.replace('H', ''), 10);
    if (firstLevel !== 1) {
      issues.push({
        type: 'warning',
        message: `First heading is ${headings[0].tagName}. Start with an H1 for hierarchy.`,
      });
    }
    let previous = firstLevel;
    headings.slice(1).forEach((heading, index) => {
      const level = parseInt(heading.tagName.replace('H', ''), 10);
      if (Number.isNaN(level)) return;
      if (level > previous + 1) {
        issues.push({
          type: 'warning',
          level,
          previous,
          text: heading.textContent.trim().slice(0, 80),
          index: index + 1,
          message: `Heading jumps from H${previous} to H${level}.`,
        });
      }
      previous = level;
    });
    return issues;
  } catch (error) {
    console.warn('Heading audit failed', error);
    return [
      {
        type: 'error',
        message: 'Failed to analyse heading structure.',
      },
    ];
  }
};

export const compileAccessibilityReport = (settings, html) => {
  const contrast = buildContrastChecks(settings).filter(Boolean);
  const headingFindings = runHeadingAudit(html);
  const hasWarnings =
    contrast.some(item => !item.passes) ||
    headingFindings.some(issue => issue && issue.type !== 'info' && issue.type !== 'success');
  return {
    contrast,
    headingFindings,
    hasWarnings,
  };
};

const applyBodyContrastFix = settings => {
  if (!settings) return { settings, changed: false };
  const outcome = resolveContrastPair({
    foreground: settings.bodyTextColor,
    background: settings.bodyBackgroundColor,
    targetRatio: 4.5,
    allowBackgroundAdjust: true,
  });

  if (!outcome.changed || !outcome.target) {
    return { settings, changed: false };
  }

  const next = { ...settings };
  if (outcome.target === 'foreground') {
    next.bodyTextColor = outcome.value;
  } else if (outcome.target === 'background') {
    next.bodyBackgroundColor = outcome.value;
  }

  return { settings: next, changed: true };
};

const applyLinkContrastFix = settings => {
  if (!settings) return { settings, changed: false };
  const outcome = resolveContrastPair({
    foreground: settings.linkColor,
    background: settings.bodyBackgroundColor,
    targetRatio: 4.5,
    allowBackgroundAdjust: false,
  });

  if (!outcome.changed || outcome.target !== 'foreground') {
    return { settings, changed: false };
  }

  const next = { ...settings, linkColor: outcome.value };
  return { settings: next, changed: true };
};

const applyFooterContrastFix = settings => {
  if (!settings) return { settings, changed: false };
  const outcome = resolveContrastPair({
    foreground: settings.footerTextColor,
    background: settings.footerBackgroundColor,
    targetRatio: 4.5,
    allowBackgroundAdjust: true,
  });

  if (!outcome.changed || !outcome.target) {
    return { settings, changed: false };
  }

  const next = { ...settings };
  if (outcome.target === 'foreground') {
    next.footerTextColor = outcome.value;
  } else if (outcome.target === 'background') {
    next.footerBackgroundColor = outcome.value;
  }
  return { settings: next, changed: true };
};

const applyCtaContrastFix = (settings, index) => {
  if (!settings || !Array.isArray(settings.ctaButtons)) {
    return { settings, changed: false };
  }
  const target = settings.ctaButtons[index];
  if (!target) return { settings, changed: false };

  const outcome = resolveContrastPair({
    foreground: target.textColor,
    background: target.backgroundColor,
    targetRatio: 4.5,
    allowBackgroundAdjust: true,
  });

  if (!outcome.changed || !outcome.target) {
    return { settings, changed: false };
  }

  const nextButtons = settings.ctaButtons.map((btn, idx) => {
    if (idx !== index || !btn) return btn;
    if (outcome.target === 'foreground') {
      return { ...btn, textColor: outcome.value };
    }
    return { ...btn, backgroundColor: outcome.value };
  });

  return { settings: { ...settings, ctaButtons: nextButtons }, changed: true };
};

const applySectionContrastFix = (settings, index) => {
  if (!settings || !Array.isArray(settings.sectionTitles)) {
    return { settings, changed: false };
  }
  if (!settings.sectionTitles[index]) {
    return { settings, changed: false };
  }

  const outcome = resolveContrastPair({
    foreground: settings.sectionTitles[index]?.color,
    background: settings.bodyBackgroundColor,
    targetRatio: 4.5,
    allowBackgroundAdjust: false,
  });

  if (!outcome.changed || outcome.target !== 'foreground') {
    return { settings, changed: false };
  }

  const nextTitles = settings.sectionTitles.map((section, idx) => {
    if (idx !== index || !section) return section;
    return {
      ...section,
      color: outcome.value,
    };
  });

  return { settings: { ...settings, sectionTitles: nextTitles }, changed: true };
};

export const applyContrastFix = (settings, item) => {
  if (!item) return { settings, changed: false };
  if (item.id === 'body-text') return applyBodyContrastFix(settings);
  if (item.id === 'link-text') return applyLinkContrastFix(settings);
  if (item.id === 'footer-text') return applyFooterContrastFix(settings);
  if (item.id.startsWith('cta-')) {
    const index = parseInt(item.id.split('-')[1], 10);
    if (Number.isInteger(index)) {
      return applyCtaContrastFix(settings, index);
    }
  }
  if (item.id.startsWith('section-')) {
    const index = parseInt(item.id.split('-')[1], 10);
    if (Number.isInteger(index)) {
      return applySectionContrastFix(settings, index);
    }
  }
  return { settings, changed: false };
};

export const canFixContrastItem = (item, settings) => {
  if (!item || !settings) return false;
  if (item.id === 'body-text') {
    return Boolean(settings.bodyBackgroundColor);
  }
  if (item.id === 'link-text') {
    return Boolean(settings.bodyBackgroundColor);
  }
  if (item.id === 'footer-text') {
    return Boolean(settings.footerBackgroundColor);
  }
  if (item.id.startsWith('cta-')) {
    const index = parseInt(item.id.split('-')[1], 10);
    return (
      Array.isArray(settings.ctaButtons) &&
      Number.isInteger(index) &&
      !!settings.ctaButtons[index]
    );
  }
  if (item.id.startsWith('section-')) {
    const index = parseInt(item.id.split('-')[1], 10);
    return (
      Array.isArray(settings.sectionTitles) &&
      Number.isInteger(index) &&
      !!settings.sectionTitles[index]
    );
  }
  return false;
};

export const applyHeadingFix = settings => {
  if (!settings || !Array.isArray(settings.sectionTitles) || settings.sectionTitles.length === 0) {
    return { settings, changed: false };
  }
  let changed = false;
  let previousLevel = 0;
  const nextTitles = settings.sectionTitles.map((section, index) => {
    if (!section) return section;
    const desiredLevel = index === 0 ? 1 : Math.min((previousLevel || 1) + 1, 6);
    previousLevel = desiredLevel;
    if (Number(section.level) === desiredLevel) {
      return section;
    }
    changed = true;
    return {
      ...section,
      level: desiredLevel,
    };
  });
  if (!changed) {
    return { settings, changed: false };
  }
  return { settings: { ...settings, sectionTitles: nextTitles }, changed: true };
};

export const canFixHeadingIssues = (headingFindings, settings) => {
  if (!Array.isArray(headingFindings) || headingFindings.length === 0) return false;
  return Array.isArray(settings?.sectionTitles) && settings.sectionTitles.length > 0;
};
